// /teams/:teamId — team home. Lists the team's projects with a quick
// summary and a create-project affordance. Mirrors the V1 shape brief: the
// team home doubles as the project picker.

import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { Button } from '../../components/ui/button.jsx'
import { useProjects, useTeam } from '../../lib/data-hooks.js'
import { createProject } from '../../lib/mutations.js'

export function TeamHome() {
  const { teamId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: team } = useTeam(teamId)
  const { data: projects, loading, error } = useProjects(teamId)

  // ProjectSwitcher's "Create new project" deep-links here with ?create=1 so
  // the inline form opens without an extra click. See #65.
  const [creating, setCreating] = useState(searchParams.get('create') === '1')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)

  async function handleCreate(e) {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    setFormError(null)
    try {
      const projectId = await createProject({ teamId, name: name.trim() })
      navigate(`/teams/${teamId}/projects/${projectId}`)
    } catch (err) {
      setFormError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Teams', to: '/teams' }, { label: team?.name ?? teamId }]}
        title={team?.name ?? teamId}
        subhead="Projects in this team"
        actions={
          !creating && (
            <Button onClick={() => setCreating(true)} variant="primary" size="sm">
              Create project
            </Button>
          )
        }
      />

      <div className="mx-auto max-w-4xl px-6 py-8">
        {creating && (
          <form
            onSubmit={handleCreate}
            className="mb-8 rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-5"
          >
            <h2 className="mb-3 text-sm font-medium text-[var(--color-basalt)]">New project</h2>
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
                Project name
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Marketing site"
                className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
              />
            </label>
            {formError && (
              <p className="mt-2 text-xs text-[var(--color-failed)]" role="alert">
                {formError}
              </p>
            )}
            <div className="mt-4 flex gap-2">
              <Button
                type="submit"
                disabled={submitting || !name.trim()}
                variant="primary"
                size="sm"
              >
                {submitting ? 'Creating…' : 'Create project'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCreating(false)
                  setName('')
                  setFormError(null)
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {loading && <p className="text-sm text-[var(--color-schist)]">Loading projects…</p>}
        {error && (
          <p className="text-sm text-[var(--color-failed)]" role="alert">
            {error.message}
          </p>
        )}
        {!loading && projects.length === 0 && !creating && (
          <div className="rounded-md border border-dashed border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/30 p-8 text-center">
            <p className="mb-4 text-sm text-[var(--color-slate)]">
              No projects yet. A project is one app you're testing.
            </p>
            <Button onClick={() => setCreating(true)} variant="primary" size="sm">
              Create your first project
            </Button>
          </div>
        )}

        {projects.length > 0 && (
          <ul className="divide-y divide-[var(--color-sandstone)] rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)]">
            {projects.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/teams/${teamId}/projects/${p.id}/flows`}
                  className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-[var(--color-sandstone)]/40"
                >
                  <div>
                    <div className="font-medium text-[var(--color-basalt)]">{p.name ?? p.id}</div>
                    <div className="font-mono text-xs text-[var(--color-schist)]">{p.id}</div>
                  </div>
                  <span className="text-[var(--color-schist)]" aria-hidden="true">
                    →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
